import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

/**
 * Shared Leaflet + OpenStreetMap map primitive for every Kilat-branded
 * surface that plots outlets (find directory, operator dashboard, agent
 * PWA, super-admin network map).
 *
 * Cost + branding rationale:
 *   - OSM raster tiles: no API key, no per-load fee (cost-model discipline).
 *     Swap behind a paid-tile env later without touching callers.
 *   - Markers are themed `divIcon` pins whose colour flows from CSS custom
 *     properties (`--geomap-tone-*`, falling back to accessible status
 *     colours), so the pins pick up each app's theme tokens — no hard-coded
 *     brand identity baked into the primitive.
 *   - Leaflet is client-only: every Leaflet call lives inside an effect and
 *     the module is only imported by client-rendered SPA routes, mirroring
 *     how `apps/find` already guards it.
 */

/** Semantic colour category for a marker. Callers map their domain status to a tone. */
export type GeoMarkerTone = 'primary' | 'positive' | 'warning' | 'danger' | 'neutral';

export interface GeoMarker {
  /** Stable id — also passed to `onMarkerClick`. */
  readonly id: string;
  readonly lat: number;
  readonly lng: number;
  /** Hover tooltip text (always shown). */
  readonly label: string;
  /** Colour category; defaults to `primary` when omitted. */
  readonly tone?: GeoMarkerTone;
  /** Rich click-popup content. When present, clicking opens the popup. */
  readonly popup?: ReactNode;
}

export interface GeoMapLegendItem {
  readonly tone: GeoMarkerTone;
  readonly label: string;
}

export interface GeoMapProps {
  readonly markers: readonly GeoMarker[];
  /** Accessible region label — required (never a silent map). */
  readonly ariaLabel: string;
  /** Pixel height (number) or any CSS length (string). Default 320. */
  readonly height?: number | string;
  /** Fires on marker click for markers WITHOUT a popup (e.g. navigate). */
  readonly onMarkerClick?: (id: string) => void;
  /** Optional tone legend rendered as an overlay card. */
  readonly legend?: readonly GeoMapLegendItem[];
  /** Clamp for auto-fit zoom so a single/clustered set is not over-zoomed. Default 15. */
  readonly fitMaxZoom?: number;
  /** Fallback centre when there are no markers. Default Kuala Lumpur. */
  readonly emptyCenter?: readonly [number, number];
  /** Fallback zoom when there are no markers. Default 10. */
  readonly emptyZoom?: number;
}

/** Kuala Lumpur — fallback view when no markers have pins yet. */
const DEFAULT_CENTER: L.LatLngTuple = [3.14, 101.69];

/**
 * Tone → colour expression. Each resolves a per-app CSS custom property first
 * (so a theme can override), then falls back to a fixed, WCAG-legible status
 * colour. Used as an inline `style` on the SVG path (presentation attributes
 * do not accept `var()`, but inline `style` does).
 */
const TONE_COLOR: Record<GeoMarkerTone, string> = {
  primary: 'var(--geomap-tone-primary, var(--color-primary, #2563eb))',
  positive: 'var(--geomap-tone-positive, #16a34a)',
  warning: 'var(--geomap-tone-warning, #d97706)',
  danger: 'var(--geomap-tone-danger, #dc2626)',
  neutral: 'var(--geomap-tone-neutral, #64748b)',
};

/** Legend swatch colours mirror the pin fills (used in the React overlay). */
const TONE_SWATCH: Record<GeoMarkerTone, string> = TONE_COLOR;

const iconCache = new Map<GeoMarkerTone, L.DivIcon>();

function pinIcon(tone: GeoMarkerTone): L.DivIcon {
  const cached = iconCache.get(tone);
  if (cached) return cached;
  // `className` overrides Leaflet's default `leaflet-div-icon` (which paints a
  // white box + border), leaving a transparent, chrome-free SVG pin.
  const icon = L.divIcon({
    className: 'kilat-geo-pin',
    html: `<svg width="26" height="34" viewBox="0 0 26 34" aria-hidden="true" focusable="false">
<path style="fill:${TONE_COLOR[tone]}" stroke="#ffffff" stroke-width="1.5" d="M13 0.75C6.4 0.75 1 6.1 1 12.7c0 8.7 12 20.3 12 20.3s12-11.6 12-20.3C25 6.1 19.6 0.75 13 0.75Z"/>
<circle cx="13" cy="12.7" r="4.4" fill="#ffffff" fill-opacity="0.92"/>
</svg>`,
    iconSize: [26, 34],
    iconAnchor: [13, 33],
    popupAnchor: [0, -30],
    tooltipAnchor: [0, -28],
  });
  iconCache.set(tone, icon);
  return icon;
}

/** Small self-contained stylesheet: round + theme the popup chrome once. */
const STYLE_ID = 'kilat-geomap-styles';
function ensureStyles(): void {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = `
.leaflet-container{font:inherit;background:var(--color-surface-2,#e5e7eb);}
.leaflet-popup-content-wrapper{border-radius:var(--radius-lg,14px);box-shadow:var(--shadow-2,0 10px 30px var(--color-border-strong));}
.leaflet-popup-content{margin:var(--space-3,12px);line-height:1.4;}
.kilat-geo-pin{background:transparent;border:0;}
`;
  document.head.appendChild(el);
}

interface PopupEntry {
  readonly id: string;
  readonly el: HTMLElement;
  readonly node: ReactNode;
}

/**
 * Renders a Leaflet/OSM map with themed pins, auto-fitting bounds to the
 * markers (or a sensible default view when empty). Popups accept arbitrary
 * `ReactNode` via portals, so links/buttons inside them keep React context
 * (router, i18n).
 */
export function GeoMap(props: GeoMapProps) {
  const {
    markers,
    ariaLabel,
    height = 320,
    onMarkerClick,
    legend,
    fitMaxZoom = 15,
    emptyCenter,
    emptyZoom = 10,
  } = props;

  const divRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const onMarkerClickRef = useRef(onMarkerClick);
  onMarkerClickRef.current = onMarkerClick;
  const [popups, setPopups] = useState<readonly PopupEntry[]>([]);

  const fallbackCenter: L.LatLngTuple = useMemo(
    () => (emptyCenter ? [emptyCenter[0], emptyCenter[1]] : DEFAULT_CENTER),
    [emptyCenter],
  );

  // Init once.
  useEffect(() => {
    if (!divRef.current || mapRef.current) return;
    ensureStyles();
    const map = L.map(divRef.current, { scrollWheelZoom: false });
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
    // A route may mount the map before layout settles; re-measure next frame.
    const raf = requestAnimationFrame(() => map.invalidateSize());
    return () => {
      cancelAnimationFrame(raf);
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // (Re)draw markers whenever the set changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const layer = L.layerGroup().addTo(map);
    const points: L.LatLngTuple[] = [];
    const nextPopups: PopupEntry[] = [];

    for (const m of markers) {
      const pos: L.LatLngTuple = [m.lat, m.lng];
      points.push(pos);
      const marker = L.marker(pos, { icon: pinIcon(m.tone ?? 'primary') }).addTo(layer);
      // SECURITY: bind a text NODE, never a raw string — Leaflet assigns string
      // tooltip content via innerHTML (stored-XSS sink). `label` is operator-
      // controlled (outlet/business name, no charset filter), viewed by
      // super-admin + agent. A text node takes Leaflet's safe appendChild path.
      const tooltipEl = document.createElement('span');
      tooltipEl.textContent = m.label;
      marker.bindTooltip(tooltipEl, { direction: 'top' });
      if (m.popup !== undefined && m.popup !== null) {
        const el = document.createElement('div');
        marker.bindPopup(el, { closeButton: true, minWidth: 180, maxWidth: 280 });
        nextPopups.push({ id: m.id, el, node: m.popup });
      } else {
        marker.on('click', () => onMarkerClickRef.current?.(m.id));
      }
    }

    if (points.length > 0) {
      map.fitBounds(L.latLngBounds(points).pad(0.25), { maxZoom: fitMaxZoom });
    } else {
      map.setView(fallbackCenter, emptyZoom);
    }
    setPopups(nextPopups);

    return () => {
      layer.remove();
    };
  }, [markers, fitMaxZoom, fallbackCenter, emptyZoom]);

  const heightStyle = typeof height === 'number' ? `${height}px` : height;

  return (
    <div style={{ position: 'relative' }}>
      <div
        ref={divRef}
        role="region"
        aria-label={ariaLabel}
        style={{
          height: heightStyle,
          borderRadius: 'var(--radius-lg, 14px)',
          overflow: 'hidden',
          boxShadow: 'var(--shadow-1, 0 1px 3px var(--color-border-strong))',
        }}
      />
      {legend && legend.length > 0 ? <MapLegend items={legend} /> : null}
      {popups.map((p) => createPortal(p.node, p.el, p.id))}
    </div>
  );
}

const legendCardStyle: CSSProperties = {
  position: 'absolute',
  left: 'var(--space-2, 8px)',
  bottom: 'var(--space-2, 8px)',
  zIndex: 500,
  background: 'var(--color-surface, #ffffff)',
  color: 'var(--color-on-surface, #0f172a)',
  borderRadius: 'var(--radius-md, 10px)',
  boxShadow: 'var(--shadow-2, 0 6px 18px var(--color-border-strong))',
  padding: 'var(--space-2, 8px) var(--space-3, 12px)',
  display: 'grid',
  gap: 4,
  fontSize: 'var(--text-meta, 0.8125rem)',
  maxWidth: '60%',
};

function MapLegend({ items }: { readonly items: readonly GeoMapLegendItem[] }) {
  return (
    <div style={legendCardStyle} aria-hidden>
      {items.map((it) => (
        <span key={it.tone} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: TONE_SWATCH[it.tone],
              flex: '0 0 auto',
            }}
          />
          {it.label}
        </span>
      ))}
    </div>
  );
}
